/**
 * useProviderInstanceAutoProvision — provisionamento automático de instância
 * durante a criação de uma caixa de entrada.
 *
 * OBJETIVO:
 *   Quando o tenant ainda possui cota livre (plano + add-ons pagos) mas nenhuma
 *   instância `released` do provedor escolhido, o sistema cria a instância
 *   automaticamente em vez de empurrar o usuário para a página de planos.
 *
 * REGRAS REAPROVEITADAS (nenhuma regra nova de cobrança/limite):
 *   - `get_tenant_instance_quota_by_provider` → decide se há vaga.
 *   - `tenant_create_instance_from_addon`     → cria a instância (valida plano
 *     integracao_v1, cota, add-on pago, provider ativo e loga bypass).
 *
 * RELEASE:
 *   2026-08-08 v1.0.0 - Versão inicial (criação automática no modal de inbox).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('ProviderInstanceAutoProvision');

/** Linha de cota retornada pela RPC de quota por provider */
interface ProviderQuotaRow {
  provider_id: string;
  provider_name: string;
  display_name: string | null;
  channel_type: string;
  allowed_qty: number;
  in_use_qty: number;
  available_qty: number;
}

/** Instância recém-criada (campos usados para vincular à inbox) */
export interface ProvisionedInstance {
  id: string;
  token: string | null;
  instance_id: string | null;
  instance_name: string | null;
  credentials: unknown;
}

interface Options {
  tenantId?: string;
  /** Provider resolvido no modal (null quando ainda não escolhido) */
  providerId?: string | null;
  /** Só consulta a cota quando realmente faz sentido (canal exige instância e não há nenhuma) */
  enabled: boolean;
}

export function useProviderInstanceAutoProvision({ tenantId, providerId, enabled }: Options) {
  const queryClient = useQueryClient();

  // ---------------------------------------------------------------------------
  // Cota disponível do provider selecionado
  // ---------------------------------------------------------------------------
  const quotaQuery = useQuery({
    queryKey: ['tenant-instance-quota', tenantId],
    enabled: enabled && !!tenantId && !!providerId,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<ProviderQuotaRow[]> => {
      const { data, error } = await supabase.rpc(
        'get_tenant_instance_quota_by_provider' as never,
        { p_tenant_hint: tenantId } as never,
      );
      if (error) {
        log.error('Erro ao buscar cota por provider', { error: error.message });
        throw error;
      }
      return (data || []) as ProviderQuotaRow[];
    },
  });

  // Vaga disponível para o provider corrente
  const availableQty =
    (quotaQuery.data || []).find((r) => r.provider_id === providerId)?.available_qty ?? 0;

  // ---------------------------------------------------------------------------
  // Criação automática da instância
  // ---------------------------------------------------------------------------
  const autoProvision = useMutation({
    mutationFn: async (targetProviderId: string): Promise<ProvisionedInstance> => {
      log.info('Criando instância automaticamente', { tenantId, targetProviderId });

      const { data: newId, error } = await supabase.rpc(
        'tenant_create_instance_from_addon' as never,
        { p_provider_id: targetProviderId, p_tenant_hint: tenantId } as never,
      );

      if (error) {
        log.error('Falha na criação automática de instância', { error: error.message });
        throw error;
      }

      // Busca os dados da instância criada para vincular à inbox
      const { data: row, error: rowError } = await supabase
        .from('provider_instances')
        .select('id, token, instance_id, instance_name, credentials')
        .eq('id', newId as unknown as string)
        .single();

      if (rowError || !row) {
        log.error('Instância criada mas não pôde ser lida', { error: rowError?.message });
        throw rowError || new Error('Instância criada, mas não foi possível carregá-la');
      }

      return row as ProvisionedInstance;
    },
    onSuccess: () => {
      // Mantém em sincronia todas as telas que exibem instâncias/cotas
      queryClient.invalidateQueries({ queryKey: ['available-instances'] });
      queryClient.invalidateQueries({ queryKey: ['pending-instances'] });
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-instance-quota'] });
      queryClient.invalidateQueries({ queryKey: ['resource-limits'] });
    },
  });

  return {
    /** Há vaga livre para criar automaticamente uma instância deste provider */
    hasQuota: availableQty > 0,
    availableQty,
    isLoadingQuota: quotaQuery.isLoading,
    autoProvision,
  };
}
